import { getBearing, getDistance, getRadialCoordinates } from "../../plugins/opsctrl-foc/functions";
import { pmtilesElevAt, pmtilesMaxElevAt } from "./terrain-pmtiles";

export type ElevationKind = "max" | "base";

export interface SampleOptions {
  stepM?: number;
  kind?: ElevationKind;
  maxSamples?: number;
}

export interface ElevationSample {
  distM: number;
  elevFt: number | null;
}

export interface ElevationProfile {
  samples: ElevationSample[];
  maxFt: number;
  totalDistM: number;
}

const METER2FEET = 3.28084;
const METER2NM = 1 / 1852;
const DEFAULT_STEP_M = 50;
const MIN_SAMPLES_PER_LEG = 8;
const MAX_SAMPLES = 20_000;
// Cap simultaneous terrain lookups: each one can trigger a PMTiles range
// fetch + WebP/canvas decode, so an unbounded Promise.all over hundreds of
// samples can stall the briefing flow / overload the loopback tile server.
const ELEV_CONCURRENCY = 16;

interface RoutePoint {
  geoPos: number[];
  totalDistance: number;
}

/** Map `fn` over `items` with at most `limit` calls in flight at once. */
export async function mapWithConcurrency<T, R>(items: T[], limit: number, fn: (item: T) => Promise<R>): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let next = 0;
  const worker = async (): Promise<void> => {
    while (next < items.length) {
      const idx = next++;
      results[idx] = await fn(items[idx]);
    }
  };
  const workers: Promise<void>[] = [];
  for (let i = 0; i < Math.min(limit, items.length); i++) workers.push(worker());
  await Promise.all(workers);
  return results;
}

/**
 * Samples max-elevation along a navlog at fixed ground-distance intervals
 * using the DEM packs' max-elevation pyramid (`pmtilesMaxElevAt`). Returns
 * null if no covering max-elev pack is installed (every sample misses).
 */
export async function sampleElevationAlongRoute(
  legs: RoutePoint[],
  opts: SampleOptions = {}
): Promise<ElevationProfile | null> {
  const stepM = opts.stepM ?? DEFAULT_STEP_M;
  const lookup = opts.kind === "base" ? pmtilesElevAt : pmtilesMaxElevAt;
  if (!legs || legs.length < 2) return null;

  const totalDistM = legs[legs.length - 1].totalDistance;
  if (!Number.isFinite(totalDistM) || totalDistM <= 0) return null;

  const sampleCap = Math.min(MAX_SAMPLES, opts.maxSamples ?? MAX_SAMPLES);
  const targetCount = Math.min(sampleCap, Math.max(legs.length, Math.ceil(totalDistM / stepM)));
  const effectiveStepM = totalDistM / targetCount;

  const queries: { distM: number; lon: number; lat: number }[] = [];

  for (let i = 0; i < legs.length - 1; i++) {
    const from = legs[i];
    const to = legs[i + 1];
    const segDist = to.totalDistance - from.totalDistance;
    if (segDist <= 0) continue;
    const brg = getBearing(from.geoPos, to.geoPos);

    const steps = Math.max(MIN_SAMPLES_PER_LEG, Math.ceil(segDist / effectiveStepM));
    for (let s = 0; s < steps; s++) {
      const t = s / steps;
      const dAlong = segDist * t;
      // getRadialCoordinates expects distance in nautical miles (earthRadius
      // in this codebase is 3440.07 nm); navLog_legs.totalDistance is metres.
      const [lon, lat] = getRadialCoordinates(from.geoPos, brg, dAlong * METER2NM);
      queries.push({ distM: from.totalDistance + dAlong, lon, lat });
    }
  }

  // The per-segment loop uses t in [0, 1), so the final route point is never
  // sampled there — append it once, unconditionally, so a zero-length last
  // segment can't truncate the graph at the destination.
  const endLeg = legs[legs.length - 1];
  queries.push({ distM: endLeg.totalDistance, lon: endLeg.geoPos[0], lat: endLeg.geoPos[1] });

  if (queries.length === 0) return null;

  const elevsM = await mapWithConcurrency(queries, ELEV_CONCURRENCY, (q) => lookup(q.lon, q.lat));

  let hits = 0;
  let maxFt = Number.NEGATIVE_INFINITY;
  const samples: ElevationSample[] = queries.map((q, idx) => {
    const eM = elevsM[idx];
    if (eM == null) return { distM: q.distM, elevFt: null };
    hits++;
    const ft = Math.round(eM * METER2FEET);
    if (ft > maxFt) maxFt = ft;
    return { distM: q.distM, elevFt: ft };
  });

  if (hits === 0) return null;

  // hits > 0 guarantees maxFt was assigned; guard only against the
  // impossible-but-typed NEGATIVE_INFINITY leaking into the profile.
  return { samples, maxFt: Number.isFinite(maxFt) ? maxFt : 0, totalDistM };
}
